import React, {Suspense, useEffect, useMemo, useState} from "react";
import {useRouter} from "next/router";
import {AppSidebar, AppHeader} from "../index";
import {
  CContainer,
  CSpinner,
  CBreadcrumb,
  CBreadcrumbItem,
  CButton,
} from "@coreui/react";
import SidebarContext from "../../hooks/SidebarContext";
import useSidebar from "../../hooks/useSidebar";
import AppBreadcrumb from "../AppBreadcrumb";
import {ToastProvider} from "../toast/ToastContainer";
import { useAuth } from "../../auth";
import {AxiosProvider, useAxios} from "../../http-client/AxiosContext";
import {hasAppAccess} from "../../auth/scopes";
import NoAccess from "../NoAccess";
import {PageHeaderProvider, usePageHeaderTitle} from "../../hooks/PageHeaderContext";
import BottomNav from "../BottomNav";
import { MobileActionProvider } from "../../hooks/MobileActionContext";
import {
  MobileUserSlotProvider,
  useMobileUserSlotClaimed,
} from "../../hooks/MobileUserSlotContext";

// On mobile, non-list pages have no search row to push content below the top
// scroll-frost + floating user button, so their first content would sit under
// the frost. Add a top gap there AND surface the page title (the mobile shell
// has no header bar) so every view names itself. List pages (search row claims
// the slot) are already offset by the row and render their title in
// MobileListControls instead.
const MobileTopGap = () => {
  const claimed = useMobileUserSlotClaimed();
  const title = usePageHeaderTitle();
  if (claimed) return null;
  return (
    <div className="tw:lg:hidden">
      <div aria-hidden className="tw:h-20" />
      {title && (
        <h1 className="tw:mb-3 tw:px-1 tw:text-xl tw:font-semibold tw:tracking-tight tw:text-text">
          {title}
        </h1>
      )}
    </div>
  );
};

// Renders the app shell only for users whose token carries app permissions.
// A default-permissions account (e.g. fresh Google SSO signup) is
// authenticated but has no read:/write:/delete: scopes — it gets the
// NoAccess screen instead of sidebar/nav/data. The API enforces scopes
// server-side regardless; this gate is UX, not the security boundary.
const PermissionGate = ({children, shell}) => {
  const {grantedScopes} = useAxios();

  if (!hasAppAccess(grantedScopes)) {
    return <NoAccess/>;
  }
  return shell(children);
};

const DefaultLayout = ({children}) => {
  const {sidebarOpen, setSidebarOpen, toggleSidebar} = useSidebar();
  const router = useRouter();
  const {isLoading, isAuthenticated, error, user, login, logout} =
    useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  if(isAuthenticated) {
    const shell = (content) => (
      <PageHeaderProvider>
        <SidebarContext.Provider
          value={{sidebarOpen, setSidebarOpen, toggleSidebar}}
        >
          <MobileActionProvider>
          <MobileUserSlotProvider>
          <div className="tw:min-h-dvh tw:app-bg tw:text-text">
            <AppSidebar/>

            {/* Offset content past the fixed 60-wide rail on desktop; on mobile
                the rail is off-canvas so no offset, and we pad for the bottom
                nav. NB: the CoreUI `.wrapper` class is intentionally dropped —
                it ships UNLAYERED with `padding-left: var(--cui-sidebar-occupy-
                start, 0)` (= 0, no CSidebar now) and would override our layered
                `tw:lg:pl-60`, letting content slide under the fixed rail. */}
            <div className="tw:flex tw:min-h-dvh tw:flex-col tw:lg:pl-60 tw:pb-16 tw:lg:pb-0">
              <AppHeader/>
              <div className="tw:flex-1">
                <CContainer className="px-4 py-4" lg>
                  <MobileTopGap />
                  <div>{content}</div>
                </CContainer>
              </div>
            </div>
            {/* Scroll-edge blur at the bottom (above the floating nav). The top
                blur lives in AppHeader so it's omitted when the header is. */}
            <div className="tw:pointer-events-none tw:fixed tw:inset-x-0 tw:bottom-0 tw:z-10 tw:h-20 tw:edge-blur-bottom tw:lg:hidden" />
            <BottomNav/>
          </div>
          </MobileUserSlotProvider>
          </MobileActionProvider>
        </SidebarContext.Provider>
      </PageHeaderProvider>
    );

    return (
      <div>
        <ToastProvider>
          {/* AxiosProvider fetches the token (blocking on "Autenticando...")
              and exposes grantedScopes; the gate decides shell vs NoAccess. */}
          <AxiosProvider>
            <PermissionGate shell={shell}>{children}</PermissionGate>
          </AxiosProvider>
        </ToastProvider>
      </div>
    );
  } else {
    return (
      <div className="ripple-background">
        <CButton size="lg" color="light" variant="outline" className="custom-button"
                 onClick={() =>
                   // Preserve the deep link (e.g. a QR scan to /order/<id>) across
                   // the Auth0 round-trip: redirect_uri is fixed, so the app relies
                   // on appState.returnTo, which onRedirectCallback restores.
                   login({ returnTo: router.asPath })
                 }>Log in</CButton>
        <div className="circle xxlarge shade1"></div>
        <div className="circle xlarge shade2"></div>
        <div className="circle large shade3"></div>
        <div className="circle medium shade4"></div>
        <div className="circle small shade5"></div>
        <div className="circle xsmall shade6"></div>
      </div>
    )
  }

};

export default DefaultLayout;
